import * as React from 'react'
import { useSearchParams } from 'react-router'
import { ScrollText, Search } from 'lucide-react'
import type { ApiRequestDto } from '@lib/dto/request-log.dto'
import { jsonValueSchema } from '@lib/helpers/json'
import { apiRequestQuerySchema, type ApiRequestQuery } from '@lib/input/request-log.input'
import { uuidParamSchema, type Uuid } from '@lib/validators/params'
import { PageHeader } from '@/components/PageHeader'
import { RecordFacts, RecordHeadline, RecordSection, RecordSheet } from '@/components/RecordSheet'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useMcpEnabled } from '@/hooks/useConnectedApps'
import { useApiRequest, useApiRequests } from '@/hooks/useRequestLog'
import { useApiKeys } from '@/hooks/useTenantSettings'
import { cn } from '@/lib/utils'

/** Who made the request: the API key, or for an MCP tool call, the caller "via" its AI app. */
function CallerLabel({ request, withPrefix = false }: { request: ApiRequestDto; withPrefix?: boolean }) {
  const via = request.mcp ? ` via ${request.mcp.app ?? 'an AI app'}` : ''
  if (request.key) {
    return (
      <>
        {request.key.name}
        {via}
        {withPrefix ? <span className="font-mono text-muted-foreground"> {request.key.prefix}…</span> : null}
      </>
    )
  }
  return <>{request.mcp ? `${request.mcp.actor.name}${via}` : '—'}</>
}

/** What was called: the method and path, or for an AI app, an "MCP" badge and the tool's name. */
function RequestLabel({ request }: { request: ApiRequestDto }) {
  if (!request.mcp) return <>{`${request.method} ${request.path}`}</>
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <Badge className="font-sans">
        MCP
      </Badge>
      <span className="truncate">{request.mcp.tool}</span>
    </span>
  )
}

/** The search in the URL; an invalid parameter is dropped on its own. */
function queryFrom(params: URLSearchParams): ApiRequestQuery {
  const raw: Record<string, string> = Object.fromEntries(params.entries())
  delete raw['request']
  for (let attempt = 0; attempt < 6; attempt += 1) {
    const parsed = apiRequestQuerySchema.safeParse(raw)
    if (parsed.success) return parsed.data
    for (const issue of parsed.error.issues) {
      const keys = issue.code === 'unrecognized_keys' ? issue.keys : [issue.path[0]]
      for (const key of keys) {
        if (typeof key === 'string') delete raw[key]
      }
    }
  }
  return {}
}

function statusTone(status: number): 'success' | 'warning' | 'danger' {
  if (status >= 500) return 'danger'
  if (status >= 400) return 'warning'
  return 'success'
}

/** A stored body, pretty-printed when it is JSON. Anything else stays as stored. */
function pretty(body: string): string {
  try {
    // JSON.parse is `any`. The shared JSON schema is what produces the typed value.
    const parsed = jsonValueSchema.safeParse(JSON.parse(body))
    if (!parsed.success) return body
    return JSON.stringify(parsed.data, null, 2)
  } catch {
    return body
  }
}

interface RequestViewerProps {
  id: Uuid
}

/** One request in the record tray: who made it, what came back, and (test mode) both bodies. */
function RequestViewer({ id }: RequestViewerProps) {
  const query = useApiRequest(id)
  if (query.isLoading) return <LoadingState />
  if (query.isError || !query.data) {
    return (
      <div className="p-card">
        <ErrorState message={apiErrorMessage(query.error, 'Could not load the request.')} />
      </div>
    )
  }
  const request = query.data
  return (
    <>
      <RecordHeadline
        facts={
          <RecordFacts>
            <dt className="text-muted-foreground">Request id</dt>
            <dd className="truncate font-mono select-all">{request.requestId}</dd>
            <dt className="text-muted-foreground">{request.mcp ? 'Called by' : 'Key'}</dt>
            <dd className="truncate">
              <CallerLabel request={request} withPrefix />
            </dd>
            <dt className="text-muted-foreground">Time</dt>
            <dd>
              {new Date(request.createdAt).toLocaleString()} · {request.durationMs} ms
            </dd>
            <dt className="text-muted-foreground">Client IP</dt>
            <dd className="font-mono">{request.ip ?? '—'}</dd>
            <dt className="text-muted-foreground">Error</dt>
            <dd className={cn('font-mono', request.errorCode ? 'text-danger' : null)}>{request.errorCode ?? '—'}</dd>
          </RecordFacts>
        }
      >
        <h3 className="font-mono text-body-lg font-semibold break-all">
          <RequestLabel request={request} />
        </h3>
        <Badge tone={statusTone(request.status)}>{request.status}</Badge>
      </RecordHeadline>
      {request.mode === 'test' ? (
        <>
          <RecordSection title="Request body">
            <pre className="overflow-x-auto font-mono text-caption whitespace-pre-wrap">
              {request.requestBody ? pretty(request.requestBody) : '(none)'}
            </pre>
          </RecordSection>
          <RecordSection title="Response body">
            <pre className="overflow-x-auto font-mono text-caption whitespace-pre-wrap">
              {request.responseBody ? pretty(request.responseBody) : '(none)'}
            </pre>
          </RecordSection>
        </>
      ) : (
        <p className="p-card text-caption text-muted-foreground">Bodies are kept for test requests only.</p>
      )}
    </>
  )
}

/**
 * Every API request your keys made in the mode on screen, for 30 days. Paste
 * a request id from an error response to find that exact request.
 */
export function RequestsPage() {
  const scope = useDashboardScope()
  const [params, setParams] = useSearchParams()
  const query = React.useMemo(() => queryFrom(params), [params])
  const requestParsed = uuidParamSchema.safeParse(params.get('request'))
  const selectedId = requestParsed.success ? requestParsed.data : null
  const requests = useApiRequests(query)
  const keys = useApiKeys()
  const [requestId, setRequestId] = React.useState(query.requestId ?? '')
  const [path, setPath] = React.useState(query.path ?? '')
  const modeKeys = (keys.data?.keys ?? []).filter((key) => key.mode === scope.mode)
  const callers = useMcpEnabled() ? `${scope.mode} keys and AI apps` : `${scope.mode} keys`

  // Back/forward changes the URL without touching the inputs, so reset them when
  // the searched values change (during render, not in an effect).
  const [synced, setSynced] = React.useState({ requestId: query.requestId, path: query.path })
  if (synced.requestId !== query.requestId || synced.path !== query.path) {
    setSynced({ requestId: query.requestId, path: query.path })
    setRequestId(query.requestId ?? '')
    setPath(query.path ?? '')
  }

  function update(changes: Partial<Record<keyof ApiRequestQuery | 'request', string | null>>): void {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    if (!('request' in changes)) next.delete('request')
    if (!('before' in changes)) next.delete('before')
    setParams(next)
  }

  const select = React.useCallback(
    (id: string | null, options: { replace: boolean } = { replace: false }) =>
      setParams((current) => {
        const next = new URLSearchParams(current)
        if (id) next.set('request', id)
        else next.delete('request')
        return next
      }, options),
    [setParams],
  )

  function renderList(rows: ApiRequestDto[]) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Request</TableHead>
            <TableHead>Key</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Error</TableHead>
            <TableHead className="text-right">Time</TableHead>
            <TableHead>When</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              data-record-id={row.id}
              className={cn('cursor-pointer', row.id === selectedId && 'bg-selected')}
              onClick={() => select(row.id)}
            >
              <TableCell className="max-w-md">
                <button type="button" className="block w-full truncate text-left font-mono text-caption text-link">
                  <RequestLabel request={row} />
                </button>
              </TableCell>
              <TableCell className="max-w-48 truncate">
                <CallerLabel request={row} />
              </TableCell>
              <TableCell>
                <Badge tone={statusTone(row.status)}>{row.status}</Badge>
              </TableCell>
              <TableCell className="font-mono text-caption text-muted-foreground">{row.errorCode ?? '—'}</TableCell>
              <TableCell className="text-right text-muted-foreground">{row.durationMs} ms</TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">{new Date(row.createdAt).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  function renderBody() {
    if (requests.isLoading) return <LoadingState />
    if (requests.isError || !requests.data) {
      return <ErrorState message={apiErrorMessage(requests.error, 'Could not load the request log.')} />
    }
    if (requests.data.requests.length === 0 && !query.before) {
      return (
        <EmptyState
          icon={ScrollText}
          title="No requests"
          description={
            Object.keys(query).length > 0
              ? 'Nothing matches this search.'
              : `Requests made with your ${callers} appear here for 30 days.`
          }
        />
      )
    }
    const nextBefore = requests.data.nextBefore
    const rows = requests.data.requests
    return (
      <div className="space-y-3">
        {renderList(rows)}
        <div className="flex justify-end gap-2">
          {query.before ? (
            <Button variant="ghost" size="sm" onClick={() => update({ before: null })}>
              Back to newest
            </Button>
          ) : null}
          {nextBefore ? (
            <Button variant="secondary" size="sm" onClick={() => update({ before: nextBefore })}>
              Older requests
            </Button>
          ) : null}
        </div>
        <RecordSheet title="API request" ids={rows.map((row) => row.id)} selectedId={selectedId} onSelect={select}>
          {(id) => <RequestViewer id={id} />}
        </RecordSheet>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title="API requests"
        description={`Every request your ${callers} made in the last 30 days, newest first.${scope.mode === 'test' ? ' Test requests keep their bodies.' : ''}`}
      />
      <ReadOnlyBanner />
      <form
        className="mb-4 flex flex-wrap items-end gap-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          update({ requestId: requestId.trim() || null, path: path.trim() || null })
        }}
      >
        <Field label="Request id" htmlFor="filter-request-id" className="w-64">
          <Input id="filter-request-id" className="font-mono" placeholder="From an error response" value={requestId} onChange={(event) => setRequestId(event.target.value)} />
        </Field>
        <Field label="Path" htmlFor="filter-path" className="min-w-40 flex-1">
          <Input id="filter-path" className="font-mono" placeholder="/v1/envelopes" value={path} onChange={(event) => setPath(event.target.value)} />
        </Field>
        <Field label="Status" htmlFor="filter-status" className="w-32">
          <Select
            id="filter-status"
            value={query.status ?? ''}
            onChange={(event) => {
              const status = apiRequestQuerySchema.shape.status.safeParse(event.target.value)
              update({ status: status.success ? (status.data ?? null) : null })
            }}
          >
            <option value="">Any</option>
            <option value="2xx">2xx</option>
            <option value="4xx">4xx</option>
            <option value="5xx">5xx</option>
          </Select>
        </Field>
        <Field label="Key" htmlFor="filter-key" className="w-48">
          <Select
            id="filter-key"
            value={query.keyId ?? ''}
            onChange={(event) => {
              const keyId = uuidParamSchema.safeParse(event.target.value)
              update({ keyId: keyId.success ? keyId.data : null })
            }}
          >
            <option value="">Any key</option>
            {modeKeys.map((key) => (
              <option key={key.id} value={key.id}>
                {key.name} ({key.prefix}…)
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" variant="secondary">
          <Search />
          Search
        </Button>
      </form>
      {renderBody()}
    </>
  )
}
